// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi, getGoogleAuthUrl, getAppleAuthUrl } from '../services/api';
import { LanguageProvider, useLang } from '../applicant/context/LanguageContext';
import { FormProvider } from '../applicant/context/FormContext';
import { ALL_DIAL_CODES, PRIORITY_CODES_SET, PRIORITY_DIAL_CODES } from '../constants/phoneDialCodes';
import { clearApplicantAuthSession, markApplicantSessionStarted } from '../utils/applicantSession';
import BrandLockup from '../components/BrandLockup';

const APPLICANT_ENTRY_INTENT_KEY = 'mvp_applicant_entry_intent';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z" fill="#FFC107" />
      <path d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" fill="#FF3D00" />
      <path d="M24 44c5.4 0 10.3-2 14-5.2l-6.5-5.5C29.5 35.1 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8H6.3C9.6 35.6 16.2 44 24 44z" fill="#4CAF50" />
      <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C37.4 38.5 44 33.5 44 24c0-1.3-.1-2.7-.4-3.9z" fill="#1976D2" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 814 1000" fill="currentColor" aria-hidden>
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.8 135.4-317.5 269-317.5 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49.1 188.3-49.1 30.3 0 130.3 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

function flagToIso2(flag) {
  const parts = [...String(flag || '')];
  if (parts.length < 2) return '';
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return '';
  if (a < base || a > base + 25 || b < base || b > base + 25) return '';
  return String.fromCharCode((a - base) + 65, (b - base) + 65).toLowerCase();
}

function getCountryIso2(item) {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === '+1') return 'us';
  return 'un';
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function splitInternationalPhone(rawValue, fallbackCountryCode) {
  const raw = String(rawValue || '').trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, '');
  const withPlus = normalized.startsWith('+')
    ? normalized
    : normalized.startsWith('00')
      ? `+${normalized.slice(2)}`
      : '';
  if (!withPlus) return null;

  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const { code } of sorted) {
    if (withPlus.startsWith(code)) {
      return {
        dial: code,
        national: digitsOnly(withPlus.slice(code.length)),
      };
    }
  }

  return {
    dial: fallbackCountryCode || '+34',
    national: digitsOnly(withPlus),
  };
}

function LoginHeader() {
  const navigate = useNavigate();
  return (
    <header className="w-full sticky top-0 z-40 bg-white" style={{ borderTop: '2px solid #a81c1c', borderBottom: '1px solid #e2e8f0' }}>
      <div className="px-5 h-14 flex items-center">
        <button type="button" onClick={() => navigate('/')} className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
          <BrandLockup
            containerClassName="flex items-center gap-3"
            logoClassName=""
            titleClassName="text-[14px] font-bold text-slate-900 tracking-tight whitespace-nowrap"
            subtitleClassName=""
            textWrapperClassName=""
            title="Global Mobility"
            showSubtitle={false}
          />
        </button>
      </div>
    </header>
  );
}

function LoginScreen() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [countryCode, setCountryCode] = useState('+34');
  const [phone, setPhone] = useState('');
  const [showDial, setShowDial] = useState(false);
  const [dialSearch, setDialSearch] = useState('');
  const dialRef = useRef(null);
  const [otpVisible, setOtpVisible] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    const handler = (e) => {
      if (dialRef.current && !dialRef.current.contains(e.target)) {
        setShowDial(false);
        setDialSearch('');
      }
    };
    if (showDial) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDial]);

  useEffect(() => {
    if (!otpVisible || resendTimer <= 0) return undefined;
    const id = setInterval(() => setResendTimer((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpVisible, resendTimer]);

  const filtered = dialSearch
    ? ALL_DIAL_CODES.filter((c) => c.code.includes(dialSearch) || c.name.toLowerCase().includes(dialSearch.toLowerCase()))
    : ALL_DIAL_CODES.filter((c) => !PRIORITY_CODES_SET.has(c.code + c.name.split(' /')[0]));

  const currentFlag = useMemo(() => {
    const found = PRIORITY_DIAL_CODES.find((c) => c.code === countryCode) || ALL_DIAL_CODES.find((c) => c.code === countryCode);
    return found || null;
  }, [countryCode]);

  const applyRawPhoneValue = (rawValue) => {
    const split = splitInternationalPhone(rawValue, countryCode || '+34');
    if (split) {
      setCountryCode(split.dial);
      setPhone(split.national);
      return;
    }
    setPhone(digitsOnly(rawValue));
  };

  const fullPhone = `${countryCode}${digitsOnly(phone)}`;

  const handleSendCode = async () => {
    if (!String(phone || '').replace(/\D/g, '').trim()) return;
    setLoading(true);
    setError('');
    try {
      await mvpApi.requestPhoneOtp(fullPhone, 'sms', 'login');
      setOtpVisible(true);
      setResendTimer(30);
    } catch (err) {
      if (err?.response?.status === 404) setError('Phone number not registered.');
      else setError(err?.response?.data?.detail || 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!String(otp || '').trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await mvpApi.verifyPhoneOtp(fullPhone, String(otp).trim(), 'login');
      const payload = res?.data || {};
      const token = payload.token;
      const user = payload.user;
      const isFirstApplicantLogin = payload.is_first_applicant_login === true;
      if (!token) {
        setError('Invalid login response.');
        return;
      }
      localStorage.setItem('authToken', token);
      markApplicantSessionStarted();
      sessionStorage.setItem(
        APPLICANT_ENTRY_INTENT_KEY,
        isFirstApplicantLogin ? 'complete_profile' : 'login'
      );
      if (user) localStorage.setItem('userData', JSON.stringify(user));
      const role = user?.role?.value ?? user?.role;
      if (role === 'admin') navigate('/advisor', { replace: true });
      else navigate(isFirstApplicantLogin ? '/intake' : '/organizer', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || loading) return;
    setOtp('');
    setError('');
    await handleSendCode();
  };

  return (
    <div className="w-full bg-[#F8F7F4] flex flex-col items-center px-6 py-6">
      <div className="w-full max-w-[400px] flex flex-col">
        <div className="mb-5 text-center">
          <h1 className="text-[26px] font-bold text-slate-900 mb-1.5">{t('auth.signIn')}</h1>
          <p className="text-[14px] text-slate-500 leading-relaxed">{t('auth.subtitle')}</p>
        </div>

        {!otpVisible && (
          <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
            <div ref={dialRef} className="relative mb-3">
              <div className={`flex h-12 bg-white border rounded-xl transition-all ${showDial ? 'border-[#1a7a3a]/50 ring-2 ring-[#1a7a3a]/20' : 'border-slate-200 focus-within:border-[#1a7a3a]/50 focus-within:ring-2 focus-within:ring-[#1a7a3a]/20'}`}>
                <button type="button" onClick={() => setShowDial((v) => !v)} className="h-full pl-3 pr-2.5 flex items-center gap-2 border-r border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap shrink-0 rounded-l-xl">
                  <span className={`fi fi-${getCountryIso2(currentFlag)} rounded-sm`} style={{ width: '1.1rem', height: '0.82rem' }} />
                  <span className="text-[13px] font-semibold text-slate-700">{countryCode}</span>
                  {showDial ? <i className="ri-arrow-up-s-line text-slate-400 text-xs" /> : <i className="ri-arrow-down-s-line text-slate-400 text-xs" />}
                </button>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => applyRawPhoneValue(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (!pasted) return;
                    e.preventDefault();
                    applyRawPhoneValue(pasted);
                  }}
                  placeholder={t('auth.phone')}
                  style={{ fontSize: '16px' }}
                  className="flex-1 min-w-0 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent rounded-r-xl"
                />
              </div>
              {showDial && (
                <div className="absolute top-[calc(100%+4px)] left-0 w-72 bg-white border border-slate-200 rounded-xl z-50 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                  <div className="p-2 border-b border-slate-100">
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                      <i className="ri-search-line text-slate-400 text-sm" />
                      <input type="text" value={dialSearch} onChange={(e) => setDialSearch(e.target.value)} placeholder="Buscar país o código…" autoFocus style={{ fontSize: '16px', touchAction: 'manipulation' }} className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none" />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {!dialSearch && <>
                      {PRIORITY_DIAL_CODES.map((c) => (
                        <button key={`p-${c.code}-${c.name}`} type="button" onClick={() => { setCountryCode(c.code); setShowDial(false); setDialSearch(''); }} className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${c.code === countryCode ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                          <span className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`} style={{ width: '1.1rem', height: '0.82rem' }} />
                          <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                          <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                        </button>
                      ))}
                      <div className="border-t border-slate-100 my-1" />
                    </>}
                    {filtered.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400 text-center">Sin resultados</div> : filtered.map((c) => (
                      <button key={`a-${c.code}-${c.name}`} type="button" onClick={() => { setCountryCode(c.code); setShowDial(false); setDialSearch(''); }} className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${c.code === countryCode ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                        <span className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`} style={{ width: '1.1rem', height: '0.82rem' }} />
                        <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                        <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" onClick={handleSendCode} disabled={loading} className="w-full min-h-[48px] text-white font-semibold text-[15px] rounded-xl flex items-center justify-center cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50" style={{ background: '#1a7a3a' }}>
              {loading ? '...' : t('auth.sendCode')}
            </button>
          </div>
        )}

        {otpVisible && (
          <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4 flex flex-col items-center gap-4">
            <div className="text-center space-y-1.5 w-full">
              <p className="text-[15px] font-semibold text-slate-800 leading-snug">{t('auth.otpIntroTitle')}</p>
              <p className="text-[14px] text-slate-600 leading-relaxed">{t('auth.otpIntroBody')}</p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder={t('auth.sixDigitCodePlaceholder')}
              className={`w-full min-h-[52px] text-center rounded-xl border px-5 py-3 outline-none transition-all bg-white text-slate-900 border-slate-200 focus:border-[#1a7a3a]/50 focus:ring-2 focus:ring-[#1a7a3a]/20 placeholder:text-slate-400 ${
                otp ? 'text-2xl font-semibold tracking-[0.45em]' : 'text-[15px] tracking-normal'
              }`}
              style={{ outlineColor: '#1a7a3a' }}
            />
            <button type="button" onClick={handleVerify} disabled={loading} className="w-full min-h-[48px] text-white font-semibold text-[15px] rounded-xl flex items-center justify-center cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50" style={{ background: '#1a7a3a' }}>
              {loading ? '...' : t('auth.verify')}
            </button>
            {resendTimer > 0 ? (
              <p className="text-[13px] text-slate-500 whitespace-nowrap">{`${t('auth.resend')} (${resendTimer}s)`}</p>
            ) : (
              <button type="button" onClick={handleResend} disabled={loading} className="text-[13px] cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50" style={{ color: '#1a7a3a' }}>
                {t('auth.resend')}
              </button>
            )}
          </div>
        )}

        {!otpVisible && <>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-[1.5px] bg-slate-200 rounded-full" />
            <span className="text-[13px] text-slate-400 font-bold tracking-[0.12em] uppercase bg-[#F8F7F4] px-2">{t('auth.or')}</span>
            <div className="flex-1 h-[1.5px] bg-slate-200 rounded-full" />
          </div>
          <div className="flex flex-col gap-3 mb-6">
            <button type="button" onClick={() => { window.location.href = getGoogleAuthUrl('login'); }} className="w-full min-h-[48px] bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-medium text-[15px] rounded-xl flex items-center justify-center gap-3 cursor-pointer transition-colors whitespace-nowrap"><GoogleIcon />{t('auth.google')}</button>
            <button type="button" onClick={() => { window.location.href = getAppleAuthUrl('login'); }} className="w-full min-h-[48px] bg-slate-900 hover:bg-black text-white font-medium text-[15px] rounded-xl flex items-center justify-center gap-3 cursor-pointer transition-colors whitespace-nowrap"><AppleIcon />{t('auth.apple')}</button>
          </div>
        </>}

        {error ? <p className="text-red-600 text-sm text-center mb-3">{error}</p> : null}
        <p className="text-center text-[12px] text-slate-400 leading-relaxed">{t('auth.privacyNote')} <a href="https://globalmobility.law/politica-privacidad" target="_blank" rel="noopener noreferrer nofollow" className="text-slate-500 underline hover:text-slate-700 cursor-pointer transition-colors">Política de Privacidad</a></p>
      </div>
    </div>
  );
}

function LoginPageContent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  /** false while validating an existing `authToken` so we do not flash the sign-in form. */
  const [sessionChecked, setSessionChecked] = useState(() => !localStorage.getItem('authToken'));

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    mvpApi
      .getMe()
      .then((res) => {
        const user = res.data?.user || res.data;
        if (user) localStorage.setItem('userData', JSON.stringify(user));
        sessionStorage.setItem(APPLICANT_ENTRY_INTENT_KEY, 'login');
        const role = user?.role?.value ?? user?.role;
        if (role === 'admin') navigate('/advisor', { replace: true });
        else navigate('/organizer', { replace: true });
      })
      .catch(() => {
        clearApplicantAuthSession();
        setSessionChecked(true);
      });
  }, [navigate]);

  if (!sessionChecked) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-[#F8F7F4] items-center justify-center px-6">
        <p className="text-slate-500 text-sm">{t('signingIn')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F8F7F4]">
      <LoginHeader />
      <div className="relative w-full h-36 overflow-hidden shrink-0">
        <img src="https://readdy.ai/api/search-image?query=Madrid%20Spain%20Plaza%20Mayor%20historic%20square%20wide%20panoramic%20view%2C%20warm%20golden%20afternoon%20sunlight%2C%20ochre%20and%20terracotta%20stone%20buildings%20with%20Spanish%20baroque%20architecture%2C%20Spanish%20flag%2C%20soft%20cinematic%20haze%2C%20rich%20warm%20tones%2C%20wide%20angle%20architectural%20photography&width=1200&height=280&seq=madrid-banner-top-strip-01&orientation=landscape" alt="Madrid, España" className="w-full h-full object-cover object-top md:object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40" />
        <div className="absolute bottom-3 left-5"><p className="text-white/90 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>Madrid, España</p></div>
      </div>
      <div className="flex-1 flex md:items-start md:justify-center"><div className="w-full"><LoginScreen /></div></div>
    </div>
  );
}

export default function Login() {
  return (
    <LanguageProvider>
      <FormProvider>
        <LoginPageContent />
      </FormProvider>
    </LanguageProvider>
  );
}
